import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  RealtimeEventSchema,
  TicketResponseSchema,
  type RealtimeEventType,
} from '../../src/shared/realtime';
import { apiPost } from '../api/client';

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

function eventToKeyPrefixes(event: RealtimeEventType, slug: string): string[][] {
  switch (event) {
    case 'message.created':
    case 'message.status_changed':
      return [
        ['conversations', slug],
        ['messages', slug],
      ];
    case 'conversation.updated':
      return [['conversations', slug]];
    case 'lead.updated':
      return [['leads', slug]];
    case 'call.missed':
      return [['calls', slug]];
    case 'appointment.created':
      return [['appointments', slug]];
    case 'job.failed':
      return [['jobs', slug]];
    case 'usage.threshold':
      // usage.threshold signals a meter crossed a threshold; the billing page
      // (plan + usage meters) is the visible surface, so invalidate it too.
      return [
        ['usage', slug],
        ['billing', slug],
      ];
  }
}

export function useRealtime(slug: string): void {
  const queryClient = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const backoffRef = useRef(RECONNECT_BASE_MS);
  const slugRef = useRef(slug);
  slugRef.current = slug;

  const connect = useCallback(async () => {
    if (!mountedRef.current) return;

    let ticket: string;
    try {
      const res = await apiPost(
        `/api/w/${slugRef.current}/realtime/ticket`,
        {},
        TicketResponseSchema
      );
      ticket = res.ticket;
    } catch {
      if (mountedRef.current) {
        reconnectTimerRef.current = setTimeout(() => {
          void connect();
        }, backoffRef.current);
        backoffRef.current = Math.min(backoffRef.current * 2, RECONNECT_MAX_MS);
      }
      return;
    }

    const wsUrl = `/realtime/ws?ticket=${ticket}`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl);
    } catch {
      if (mountedRef.current) {
        reconnectTimerRef.current = setTimeout(() => {
          void connect();
        }, backoffRef.current);
        backoffRef.current = Math.min(backoffRef.current * 2, RECONNECT_MAX_MS);
      }
      return;
    }

    wsRef.current = ws;

    ws.onopen = () => {
      backoffRef.current = RECONNECT_BASE_MS;
    };

    ws.onmessage = (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(event.data as string) as unknown;
        const realtimeEvent = RealtimeEventSchema.parse(parsed);
        const prefixes = eventToKeyPrefixes(realtimeEvent.event, slugRef.current);
        for (const prefix of prefixes) {
          void queryClient.invalidateQueries({ queryKey: prefix });
        }
      } catch {
        // Malformed events are silently ignored.
      }
    };

    ws.onclose = () => {
      wsRef.current = null;
      if (mountedRef.current) {
        reconnectTimerRef.current = setTimeout(() => {
          void connect();
        }, backoffRef.current);
        backoffRef.current = Math.min(backoffRef.current * 2, RECONNECT_MAX_MS);
      }
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [queryClient]);

  useEffect(() => {
    mountedRef.current = true;
    void connect();

    return () => {
      mountedRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);
}
